import React, { FC, useCallback, useContext, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import PaginatedReduxTableWithHeader from '../common/PaginatedReduxTableWithHeader';
import { ROLE_TYPES } from '../../utils/DataConstants';
import {
	downloadWalkthroughsCSV,
	fetchWalkthroughsForBuyer,
	fetchWalkthroughsForSupplier,
	refreshWalkthroughCountsForBuyer,
	refreshWalkthroughCountsForSupplier,
} from '../../thunks/walkthroughs_thunks';
import { WALKTHROUGHS_CRUD_ACTION_CREATORS } from '../../actions/walkthroughs_actions';
import { WalkthroughRowDisplay } from '../walkthrough_row_display/WalkthroughRowDisplay';
import { getLinkWIthBackLinkParams } from '../../utils/HistoryUtils';
import { EmptyState } from '../empty_state/EmptyState';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { isInternalTech, PERMISSION_NAMES } from '../../utils/AuthUtils';
import { Button, Col } from 'antd';
import { getStateSliceProp, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import ScheduleWalkthroughModalForm from '../schedule_walkthrough_modal_form/ScheduleWalkthroughModalForm';
import WalkthroughPipeLineFilters from '../site_surveys_detail_details_page/walkthroughs_pipeline_filters';
import { DownloadOutlined } from '@ant-design/icons';
import { withUserType } from '../withUserType';
import getWalkthroughSorters from './WalkthroughSorters';
import { useFilters } from '../../hooks/useFilters';
import { getWalkthroughFilterConfig } from '../../utils/WalkthroughFilterUtils';
import { useWebSocketListener } from '../../context/useWebSocketListener';
import { WEB_SOCKET_ENTITY_TYPES, WEB_SOCKET_MESSAGE_TYPES } from '../../utils/WebSocketUtils';
import { WebSocketContext } from '../../context/WebSocketContext';

const TC_NAME = 'siteSurveyAssociatedWalkthroughs';
const WALKTHROUGH_ADVANCED_FILTERS = 'walkthroughAdvancedFilters';

const WalkthroughsIndexPage: FC<any> = ({
	updateFilters,
	updateSorters,
	clearAndUpdateFilters,
	refreshCounts,
	walkthroughs,
	location,
	history,
	userType,
	currentUser,
	fetchWalkthroughs,
	downloadWalkthroughCSV,
	refreshWalkthrough,
}): React.ReactElement => {
	const [scheduleVisible, setScheduleVisible] = useState(false);
	const socketContext = useContext(WebSocketContext);

	const entityIds = useMemo(() => {
		const recordsIds = nullSafeGetOrElse(`${TC_NAME}.recordsIds`, walkthroughs, []);
		return recordsIds.map((id) => String(id)).filter(Boolean);
	}, [walkthroughs]);

	const onWsMessage = useCallback(
		(event) => {
			try {
				const data = JSON.parse(event.data);

				if (!entityIds.includes(String(data.walkthroughId))) return;

				switch (data.messageType) {
					case WEB_SOCKET_MESSAGE_TYPES.NEW_NOTE:
						refreshWalkthrough(data.walkthroughId);
						break;
					default:
						break;
				}
			} catch (err) {
				// JSON parse error
			}
		},
		[entityIds, refreshWalkthrough]
	);

	useWebSocketListener({
		socketContext,
		onMessage: onWsMessage,
		entityName: WEB_SOCKET_ENTITY_TYPES.WALKTHROUGH,
		entityIds,
	});

	const _showSchedule = () => setScheduleVisible(true);
	const _hideSchedule = () => setScheduleVisible(false);

	const onScheduleSuccess = useCallback(() => {
		_hideSchedule();
		const filters = getStateSliceProp('filters', TC_NAME, walkthroughs);
		fetchWalkthroughs(
			{},
			TC_NAME,
			getStateSliceProp('pagination', TC_NAME, walkthroughs),
			getStateSliceProp('sorting', TC_NAME, walkthroughs),
			filters
		);
		refreshCounts(filters);
	}, [fetchWalkthroughs, refreshCounts, walkthroughs]);

	const downloadCSV = useCallback(() => {
		const filters = walkthroughs[TC_NAME].filters;
		downloadWalkthroughCSV({}, filters);
	}, [downloadWalkthroughCSV, walkthroughs]);

	const columns = useMemo(
		() => [
			{
				title: 'Name',
				dataIndex: 'title',
				render: (_, record) => <WalkthroughRowDisplay walkthrough={record} />,
			},
		],
		[]
	);

	const onWalkthroughsRow = useCallback(
		(record) => ({
			onClick: () => {
				let searchString = location.search;
				if (searchString[0] === '?') {
					searchString = searchString.slice(1);
				}
				const path = `/${userType}/siteSurveys/walkthrough/detail/${record.id}/details`;
				history.push(getLinkWIthBackLinkParams(location, 'Back to Walkthroughs', path));
			},
		}),
		[history, location, userType]
	);

	const isScheduleCTAVisible = useMemo(
		() => userType === ROLE_TYPES.BUYER || isInternalTech(currentUser),
		[userType, currentUser]
	);

	const walkthroughFilters = useFilters({
		filterTargetCollectionName: WALKTHROUGH_ADVANCED_FILTERS,
		overviewCollectionName: TC_NAME,
		updateFilters: useCallback((filters) => updateFilters(filters, TC_NAME), [updateFilters]),
	});

	const filterConfig = useMemo(
		() => getWalkthroughFilterConfig({ filters: walkthroughFilters }),
		[walkthroughFilters]
	);

	const completedFilterEnabled = useMemo(() => {
		const filters = getStateSliceProp('filters', TC_NAME, walkthroughs);
		const statuesFilter = nullSafeGetOrElse('statuses', filters, '');
		return statuesFilter.split(',').includes('complete');
	}, [walkthroughs]);

	return (
		<>
			<PaginatedReduxTableWithHeader
				targetCollectionName={TC_NAME}
				updateFilters={updateFilters}
				stateSlice={walkthroughs}
				refreshCounts={refreshCounts}
				sorters={getWalkthroughSorters(completedFilterEnabled)}
				initialSorters={{
					sort_by: 'updatedAt',
					order: 'desc',
				}}
				updateSorters={updateSorters}
				pipeLineFilters={WalkthroughPipeLineFilters}
				clearAndUpdateFilters={clearAndUpdateFilters}
				filterConfig={filterConfig}
				entityCollectionName="walkthroughs"
				tableColumns={columns}
				fetchData={
					userType === ROLE_TYPES.SUPPLIER
						? fetchWalkthroughsForSupplier
						: fetchWalkthroughsForBuyer
				}
				rightActions={
					<Col style={{ textAlign: 'right' }}>
						<Button
							style={{ marginRight: 12 }}
							size="large"
							icon={<DownloadOutlined />}
							className="inline-block-visible-md"
							onClick={downloadCSV}
						>
							<span className="inline-block-visible-xxl">Export CSV</span>
						</Button>
						{isScheduleCTAVisible ? (
							<AccessPermissionChecker name={PERMISSION_NAMES.ADD_WALKTHROUGHS}>
								<Button type="primary" size="large" onClick={_showSchedule}>
									Schedule Walkthrough
								</Button>
							</AccessPermissionChecker>
						) : null}
					</Col>
				}
				onTableRow={onWalkthroughsRow}
				emptyState={
					<EmptyState
						graphic={
							<img
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/cactus.svg"
								alt="You haven't conducted any walkthroughs yet."
							/>
						}
						headline={"You haven't conducted any walkthroughs yet."}
						body={
							<div style={{ textAlign: 'center' }}>
								<div style={{ maxWidth: 440, marginBottom: 16 }}>
									{isScheduleCTAVisible ? (
										<AccessPermissionChecker name={PERMISSION_NAMES.ADD_WALKTHROUGHS}>
											<Button type="primary" size="large" onClick={_showSchedule}>
												Schedule Walkthrough
											</Button>
										</AccessPermissionChecker>
									) : null}
								</div>
							</div>
						}
					/>
				}
			/>
			{scheduleVisible ? (
				<ScheduleWalkthroughModalForm onCancel={_hideSchedule} onSuccess={onScheduleSuccess} />
			) : null}
		</>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
	walkthroughs: state.walkthroughs,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	refreshCounts: (filters) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? refreshWalkthroughCountsForSupplier(filters)
				: refreshWalkthroughCountsForBuyer(filters)
		),
	updateFilters: (filters, targetCollection) =>
		dispatch(WALKTHROUGHS_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollection)),
	updateSorters: (sortBy, order, targetCollection) =>
		dispatch(WALKTHROUGHS_CRUD_ACTION_CREATORS.updateSorting(sortBy, order, targetCollection)),
	clearAndUpdateFilters: (filters, targetCollectionName) =>
		dispatch(
			WALKTHROUGHS_CRUD_ACTION_CREATORS.clearAndUpdateFilters(filters, targetCollectionName)
		),
	fetchWalkthroughs: (params, targetCollectionName, pagination, sorting, filters) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? fetchWalkthroughsForSupplier(params, targetCollectionName, pagination, sorting, filters)
				: fetchWalkthroughsForBuyer(params, targetCollectionName, pagination, sorting, filters)
		),
	downloadWalkthroughCSV: (params, filters) =>
		dispatch(downloadWalkthroughsCSV(ownProps.userType)(params, filters)),
	refreshWalkthrough: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? walkthroughsRestCrudThunksForSupplier.readOne(id)
				: walkthroughsRestCrudThunksForBuyer.readOne(id)
		),
});

export default withUserType(
	withRouter(connect(mapStateToProps, mapDispatchToProps)(WalkthroughsIndexPage as any)) as any
);
